import { zodResolver } from '@hookform/resolvers/zod';
import { useSelector } from '@xstate/react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import {
  AsyncSelect,
  FormLayout,
  FormRow,
  FormSection,
  LoadingSpinner,
  TextInput,
} from '~/components';
import { TransferList } from '~/components/forms/TransferList';
import { US_STATES_AND_TERRITORIES } from '~/constants';
import { useCustomForm } from '~/hooks/useCustomForm';
import { AssociatedProviderPicker } from '../../../../listing-search/sub-components/AssociatedProviderPicker';
import { useMsaasListingsActorRef } from '../../msaasProvider';
import type { Context as MsaasListingContext } from '../../types';
import { loadRosterProviderIds } from './api';
import { coversPlace, coversStates, type CoverageFormValues } from './types';
import {
  buildCoveragePayload,
  createCoverageFormSchema,
  droppedCoverageNotes,
  hydrateCoverageForm,
  loadCoverageLocations,
  WORK_LOCATION_NAME,
} from './utils';

type RosterStatus = 'loading' | 'loaded' | 'failed';

/**
 * [MCV-1889] Step 2 of a selling listing: where the seller can work (D19) and who would do it.
 *
 * Remote and hybrid listings choose license states; on-site and hybrid ones choose a place and a
 * travel distance. What the work location does not use is hidden and not saved (DD16), and shows a
 * note instead, so switching the work location back before saving brings it back.
 */
export const CoverageForm = ({ footer }: { footer: React.ReactNode }) => {
  const msaasListingsRef = useMsaasListingsActorRef();
  const { coverageStepRef, listing, employerUser } = useSelector(
    msaasListingsRef,
    (s): MsaasListingContext => s.context as MsaasListingContext
  );
  const saving = useSelector(coverageStepRef, (s) => s.context.loading);
  const workLocation = listing?.workLocation ?? 'REMOTE';
  const schema = useMemo(() => createCoverageFormSchema(workLocation), [workLocation]);

  const methods = useCustomForm<CoverageFormValues>({
    resolver: zodResolver(schema),
    defaultValues: hydrateCoverageForm(listing),
  });
  const { setValue, handleSubmit } = methods;
  const values = methods.watch();

  // The roster is read once per listing. The picker only appears once it has loaded, so a pick is
  // always made against the real roster and saving never drops members the owner did not see. A
  // failed read must not turn into "no providers": the save then leaves the roster alone.
  const [loadedRoster, setLoadedRoster] = useState<number[]>();
  const [rosterStatus, setRosterStatus] = useState<RosterStatus>(
    listing?.id ? 'loading' : 'loaded'
  );
  const listingId = listing?.id;
  useEffect(() => {
    if (!listingId) return;
    let active = true;
    setRosterStatus('loading');
    loadRosterProviderIds(listingId)
      .then((ids) => {
        if (!active) return;
        setValue('providerIds', ids);
        setLoadedRoster(ids);
        setRosterStatus('loaded');
      })
      .catch(() => active && setRosterStatus('failed'));
    return () => {
      active = false;
    };
  }, [listingId, setValue]);

  const onSubmitSuccess = (submitted: CoverageFormValues) => {
    coverageStepRef.send({
      type: 'ON_SUBMIT',
      // Only a roster that was read is written back; otherwise it is left as it is.
      payload: buildCoveragePayload(submitted, listing, rosterStatus === 'loaded', loadedRoster),
    });
  };

  if (saving) return <LoadingSpinner />;

  const notes = droppedCoverageNotes(workLocation, {
    states: values.states,
    location: values.location,
    radiusMiles: values.radiusMiles,
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitSuccess)}>
        <FormLayout>
          <p className="body-text text-grey-300" data-cy="coverage-work-location">
            Work location: <b>{WORK_LOCATION_NAME[workLocation] ?? workLocation}</b>. Buyers match
            on the coverage you list here.
          </p>
          {coversStates(workLocation) && (
            <FormSection componentsPerLine={1}>
              <FormRow>
                <TransferList
                  name="states"
                  label="License states you can cover"
                  description="Select the states where your providers are licensed to work remotely."
                  availableOptions={US_STATES_AND_TERRITORIES}
                  availableLabel="Available States & Territories"
                  selectedLabel="Selected"
                  cyTag="coverage-states"
                  filterable
                  required
                />
              </FormRow>
            </FormSection>
          )}
          {coversPlace(workLocation) && (
            <FormSection componentsPerLine={2}>
              <FormRow>
                <AsyncSelect
                  name="location"
                  cyTag="coverage-location"
                  label="Location"
                  description="The city or ZIP your team works from."
                  placeholder="City, ST or ZIP"
                  noOptionsMessage="Type at least 2 characters of a city or ZIP code"
                  loadingMessage="Searching..."
                  minChars={false}
                  defaultOptions={false}
                  cacheOptions={false}
                  filterOption={null}
                  loadOptions={loadCoverageLocations}
                  required
                />
              </FormRow>
              <FormRow>
                <TextInput
                  name="radiusMiles"
                  cyTag="coverage-radius"
                  label="Travel distance (miles)"
                  description="How far from that place you will travel."
                  type="number"
                  required
                />
              </FormRow>
            </FormSection>
          )}
          {notes.map((note) => (
            <p key={note} className="body-text text-grey-300" data-cy="coverage-dropped-note">
              {note}
            </p>
          ))}
          <div data-cy="coverage-roster">
            <h3 className="sub-heading-sb">Offered providers</h3>
            <p className="body-text text-grey-300">
              Optional. Providers on your roster let buyers see who they would work with, and verify
              your license states to raise your match.
            </p>
            {rosterStatus === 'failed' && (
              <p className="body-text text-error" role="alert">
                Your roster could not be loaded, so it will be left as it is when you continue.
              </p>
            )}
            {rosterStatus === 'loading' && <LoadingSpinner />}
            {rosterStatus === 'loaded' && (
              <Controller
                name="providerIds"
                control={methods.control}
                render={({ field }) => (
                  <AssociatedProviderPicker
                    employerId={employerUser?.employerId}
                    selectedProviderIds={field.value ?? []}
                    onChange={field.onChange}
                  />
                )}
              />
            )}
          </div>
          {footer}
        </FormLayout>
      </form>
    </FormProvider>
  );
};
